<template>
  <div class="flex flex-col gap-y-6">
    <FacialBiometryFilterBar
      ref="filterBarRef"
      @search="handleSearch"
      @filter-status="handleFilterStatus"
      @filter-date="handleFilterDate"
      @filter-schedule="handleFilterSchedule"
      @filter-unit="handleFilterUnit"
      @sort="handleSort"
    />

    <div class="flex flex-col min-h-[300px]">
      <FacialBiometryTableSkeleton v-if="isLoading" />

      <template v-else>
        <FacialBiometryTable
          v-if="doctors.length > 0"
          :doctors="doctors"
          @evaluate="handleEvaluate"
          @view-history="handleViewHistory"
          @notify="handleNotify"
        />
        <FacialBiometryEmptyState v-else @clear-filters="handleClearFilters" />
      </template>
    </div>

    <v-pagination
      v-if="!isLoading && doctors.length > 0"
      :model-value="meta.page"
      :total-visible="5"
      :length="meta.totalPages"
      density="comfortable"
      color="primary"
      data-testid="biometry-pagination"
      @update:model-value="handleChangePage"
    />

    <ValidationDetailModal v-model="showValidationModal" @validated="handleValidated" />

    <ValidationHistoryModal v-model="showHistoryModal" :doctor="selectedDoctor" />

    <ConfirmDeleteAction
      :show="showNotifyConfirmModal"
      title="Confirmar notificação"
      :description="notifyConfirmDescription"
      text-button="Notificar"
      @close-modal="closeNotifyModal"
      @handle-confirm="confirmNotify"
    />
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted } from 'vue'
import { storeToRefs } from 'pinia'
import { useFacialBiometryStore } from '@/pinia-store/useFacialBiometryStore'
import type { FacialBiometryStatus } from '@/constants/facial-biometry.constants'
import type {
  FacialBiometryDoctorItem,
  FacialBiometryListParams
} from '@/types/facial-biometry.types'
import FacialBiometryFilterBar from './components/FacialBiometryFilterBar.vue'
import FacialBiometryTable from './components/FacialBiometryTable.vue'
import FacialBiometryTableSkeleton from './components/FacialBiometryTableSkeleton.vue'
import FacialBiometryEmptyState from './components/FacialBiometryEmptyState.vue'
import ValidationDetailModal from './components/ValidationDetailModal.vue'
import ValidationHistoryModal from './components/ValidationHistoryModal.vue'
import ConfirmDeleteAction from '@/components/designSystem/modals/confirm.delete.action.vue'

const store = useFacialBiometryStore()

const { doctors, meta, isLoading } = storeToRefs(store)

const filterBarRef = ref<InstanceType<typeof FacialBiometryFilterBar> | null>(null)
const showValidationModal = ref(false)
const showHistoryModal = ref(false)
const showNotifyConfirmModal = ref(false)
const selectedDoctor = ref<FacialBiometryDoctorItem | null>(null)
const doctorToNotify = ref<FacialBiometryDoctorItem | null>(null)

const notifyConfirmDescription = computed(() => {
  if (!doctorToNotify.value) return ''
  return `Deseja notificar o médico ${doctorToNotify.value.doctor.name} para enviar a biometria facial?`
})

onMounted(async () => {
  await store.fetchDoctors()
})

function applyFilter(filters: Partial<FacialBiometryListParams>) {
  store.setFilters(filters)
  store.fetchDoctors()
}

function handleSearch(query: string) {
  applyFilter({ search: query || undefined })
}

function handleFilterStatus(value: string) {
  applyFilter({ status: value ? (value as FacialBiometryStatus) : null })
}

function handleFilterDate(payload: { start: Date | null; end: Date | null }) {
  applyFilter({
    startDate: payload.start?.toISOString(),
    endDate: payload.end?.toISOString()
  })
}

function handleFilterSchedule(value: string) {
  applyFilter({ scheduleIds: value ? [Number(value)] : undefined })
}

function handleFilterUnit(value: string) {
  applyFilter({ healthUnitIds: value ? [Number(value)] : undefined })
}

function handleSort(order: string) {
  applyFilter({ sortOrder: order as 'ASC' | 'DESC' })
}

function handleChangePage(page: number) {
  store.setPage(page)
  store.fetchDoctors(page)
}

function handleClearFilters() {
  store.resetFilters()
  filterBarRef.value?.reset()
  store.fetchDoctors()
}

function handleEvaluate(item: FacialBiometryDoctorItem) {
  store.initValidationQueue(doctors.value)

  const idx = store.validationQueue.findIndex((d) => d.id === item.id)
  if (idx >= 0) {
    store.setStepIndex(idx)
  }

  showValidationModal.value = true
}

function handleViewHistory(item: FacialBiometryDoctorItem) {
  selectedDoctor.value = item
  showHistoryModal.value = true
}

function handleNotify(item: FacialBiometryDoctorItem) {
  doctorToNotify.value = item
  showNotifyConfirmModal.value = true
}

async function confirmNotify() {
  if (!doctorToNotify.value) return
  await store.notifyDoctor(doctorToNotify.value.doctor.id)
  closeNotifyModal()
}

function closeNotifyModal() {
  showNotifyConfirmModal.value = false
  doctorToNotify.value = null
}

function handleValidated() {
  store.fetchDoctors()
}
</script>
